import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ParseKeys } from 'i18next';
import { Check, ShieldAlert, X } from 'lucide-react';
import {
  AssistantApprovalStatus,
  AssistantDecisionType,
  AssistantToolName,
  type AssistantApprovalPart,
  type AssistantDecision,
} from '@xtreed/academy-shared';
import { cn } from '@/lib/utils';
import { CoursePlanCard, type CoursePlanArgs } from './CoursePlanCard';
import { Button, Textarea } from '@xtreed/academy-shared/ui';

const DESTRUCTIVE = new Set<string>([
  AssistantToolName.ARCHIVE_COURSE,
  AssistantToolName.DELETE_COURSE,
  AssistantToolName.DELETE_MODULE,
  AssistantToolName.DELETE_LESSON,
  AssistantToolName.UNLINK_MODULE,
  AssistantToolName.UNLINK_LESSON,
]);

interface ApprovalCardProps {
  part: AssistantApprovalPart;
  disabled: boolean;
  onDecide: (decisions: AssistantDecision[]) => void;
}

function resolvedLabel(
  part: AssistantApprovalPart,
): ParseKeys<'adminAssistant'> {
  if (part.status === AssistantApprovalStatus.EXPIRED)
    return 'approval.expired';
  const type = part.decisions?.[0]?.type;
  if (type === AssistantDecisionType.REJECT) return 'approval.rejected';
  if (type === AssistantDecisionType.EDIT) return 'approval.edited';
  return 'approval.approved';
}

export function ApprovalCard({ part, disabled, onDecide }: ApprovalCardProps) {
  const { t, i18n } = useTranslation('adminAssistant');
  const pending = part.status === AssistantApprovalStatus.PENDING;
  const planIndex = part.actions.findIndex(
    (action) => action.name === AssistantToolName.APPLY_COURSE_PLAN,
  );
  const originalPlan = useMemo(() => {
    if (planIndex < 0) return null;
    const decision = part.decisions?.[planIndex];
    const args =
      decision?.type === AssistantDecisionType.EDIT
        ? decision.editedAction.args
        : part.actions[planIndex].args;
    return args as unknown as CoursePlanArgs;
  }, [part.actions, part.decisions, planIndex]);
  const [plan, setPlan] = useState<CoursePlanArgs | null>(originalPlan);
  const [adjusting, setAdjusting] = useState(false);
  const [adjustment, setAdjustment] = useState('');
  const canEdit =
    planIndex >= 0 &&
    part.actions[planIndex].allowedDecisions.includes(
      AssistantDecisionType.EDIT,
    );
  const edited =
    plan !== null && JSON.stringify(plan) !== JSON.stringify(originalPlan);
  const destructive = part.actions.some((action) =>
    DESTRUCTIVE.has(action.name),
  );

  const approve = () =>
    onDecide(
      part.actions.map((action, index) =>
        index === planIndex && edited && canEdit && plan
          ? {
              type: AssistantDecisionType.EDIT,
              editedAction: {
                name: action.name,
                args: plan as unknown as Record<string, unknown>,
              },
            }
          : { type: AssistantDecisionType.APPROVE },
      ),
    );

  const reject = (message?: string) =>
    onDecide(
      part.actions.map(() =>
        message?.trim()
          ? { type: AssistantDecisionType.REJECT, message: message.trim() }
          : { type: AssistantDecisionType.REJECT },
      ),
    );

  return (
    <div
      className={cn(
        'bubble-in w-full rounded-2xl border p-3',
        pending
          ? destructive
            ? 'border-destructive/40 bg-destructive/5'
            : 'border-primary/40 bg-primary/5'
          : 'border-border/50 bg-secondary/30',
      )}
    >
      {planIndex < 0 && (
        <div className="mb-2 flex items-start gap-2">
          <ShieldAlert
            className={cn(
              'mt-0.5 h-4 w-4 shrink-0',
              destructive ? 'text-destructive' : 'text-primary-ink',
            )}
          />
          <div className="min-w-0 space-y-0.5">
            <p className="text-sm font-semibold text-foreground">
              {t('approval.title')}
            </p>
            {part.actions.map((action, index) => {
              const key = `approval.actions.${action.name}`;
              const visibility = action.args.visibility;
              return (
                <div key={index} className="space-y-0.5">
                  <p className="text-xs text-muted-foreground">
                    {i18n.exists(`adminAssistant:${key}`)
                      ? t(key as ParseKeys<'adminAssistant'>)
                      : t('approval.actions.fallback')}
                  </p>
                  {action.description && (
                    <p className="text-sm font-medium text-foreground">
                      {action.description}
                    </p>
                  )}
                  {(visibility === 'HIDDEN' || visibility === 'LOCKED') && (
                    <p className="text-xs text-muted-foreground">
                      {t('approval.visibilityTo', {
                        value: t(`plan.visibility.${visibility}`),
                      })}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {plan && (
        <CoursePlanCard
          plan={plan}
          editable={pending && canEdit && !disabled}
          onChange={setPlan}
        />
      )}

      {pending ? (
        adjusting ? (
          <div className="mt-3 space-y-2">
            <Textarea
              autoFocus
              value={adjustment}
              onChange={(event) => setAdjustment(event.target.value)}
              placeholder={t('approval.adjustPlaceholder')}
              className="min-h-[72px] resize-none text-sm"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAdjusting(false)}
              >
                {t('approval.cancel')}
              </Button>
              <Button
                size="sm"
                disabled={disabled || !adjustment.trim()}
                onClick={() => reject(adjustment)}
              >
                {t('approval.sendAdjust')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-3 flex flex-wrap justify-end gap-2">
            {plan ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={() => setAdjusting(true)}
              >
                {t('approval.adjust')}
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={() => reject()}
              >
                <X className="mr-1.5 h-3.5 w-3.5" />
                {t('approval.reject')}
              </Button>
            )}
            <Button
              size="sm"
              variant={destructive ? 'destructive' : 'default'}
              disabled={disabled}
              onClick={approve}
            >
              <Check className="mr-1.5 h-3.5 w-3.5" />
              {t('approval.approve')}
            </Button>
          </div>
        )
      ) : (
        <p className="mt-2 text-xs font-medium text-muted-foreground">
          {t(resolvedLabel(part))}
        </p>
      )}
    </div>
  );
}
